import axios from 'axios'

import { SOURCE_TYPES } from '@/store/MonitoringFeature/monitoringfeature'

import {
  RPD_PATHS,
  buildResolveRequest,
  buildGenerateRequest,
  configurationFromPairing,
  pairingFromConfiguration,
  thresholdsFromConfiguration,
  excludedAnalyteIdsFromConfiguration,
  toAliasOptions,
  toAnalyteCatalogue,
  toJobAccepted,
  toLocationOption,
  toResolveOutcome,
  toSavedQueries,
  toTenantOptions,
} from '@/helpers/rpd'

/*
 * The RPD Report store. One module owns the whole page: the two option vocabularies, the pairing
 * gate, the generation request and the saved-query list.
 *
 * Every call is tenant- and user-scoped server side, so no action here sends a tenant or an owner.
 * Generation answers 202 with a message id and the workbook is produced out of band; the reviewer
 * follows it in the message centre, exactly as for the other asynchronous report modules, so this
 * store holds no job state and runs no poller.
 */

const GATE_IDLE = 'idle'

const initialState = () => ({
  aliasOptions: { aliases: [], mediaTypes: [], aliasLabels: {}, mediaLabels: {} },
  aliasOptionsLoaded: false,
  aliasOptionsError: null,

  /*
   * The aliases of the location each side names, held per side because the two sides are
   * independent. `null` means the side names no location, so the tenant-wide vocabulary applies.
   * Each entry records the location it was read for, so a response that lands after the reviewer
   * has moved on is recognisably stale rather than mistaken for the current one.
   */
  locationAliasOptions: { Primary: null, Duplicate: null },

  tenantOptions: {
    acceptanceThreshold: null,
    detectionLimitMultiplier: null,
    excludedAnalyteKeys: [],
    excludedAnalyteIds: [],
  },
  tenantOptionsLoaded: false,
  tenantOptionsError: null,

  /* The tenant's analytes, for the exclusion picker. Reuses the existing Analyte/List route. */
  analyteCatalogue: [],
  analyteCatalogueLoaded: false,

  /* The tenant's discrete stations, for the location pickers. Reuses the existing DataSources route. */
  locationOptions: [],
  locationOptionsLoaded: false,

  gateKind: GATE_IDLE,
  resolution: {
    primarySampleIds: [],
    duplicateSampleIds: [],
    message: null,
    isUnderConstrained: false,
  },

  resolving: false,
  generating: false,

  savedQueries: [],
  savedQueriesLoaded: false,
  /* A 503 means the store was unreachable, which is not the same as "you have saved nothing". */
  savedQueriesUnavailable: false,
  savedQuery: null,
})

export default {
  namespaced: true,

  state: initialState(),

  mutations: {
    SET_ALIAS_OPTIONS(state, options) {
      state.aliasOptions = options
      state.aliasOptionsLoaded = true
      state.aliasOptionsError = null
    },

    SET_ALIAS_OPTIONS_ERROR(state, message) {
      state.aliasOptionsError = message
    },

    SET_LOCATION_ALIAS_OPTIONS(state, { side, locationId, options }) {
      state.locationAliasOptions = {
        ...state.locationAliasOptions,
        [side]: { locationId, options },
      }
    },

    SET_TENANT_OPTIONS(state, options) {
      state.tenantOptions = options
      state.tenantOptionsLoaded = true
      state.tenantOptionsError = null
    },

    SET_TENANT_OPTIONS_ERROR(state, message) {
      state.tenantOptionsError = message
    },

    SET_ANALYTE_CATALOGUE(state, analytes) {
      state.analyteCatalogue = analytes
      state.analyteCatalogueLoaded = true
    },

    SET_LOCATION_OPTIONS(state, locations) {
      state.locationOptions = locations
      state.locationOptionsLoaded = true
    },

    SET_RESOLUTION(state, resolution) {
      state.gateKind = resolution.gateKind
      state.resolution = {
        primarySampleIds: resolution.primarySampleIds || [],
        duplicateSampleIds: resolution.duplicateSampleIds || [],
        message: resolution.message || null,
        isUnderConstrained: !!resolution.isUnderConstrained,
      }
    },

    SET_RESOLVING(state, value) {
      state.resolving = value
    },

    SET_GENERATING(state, value) {
      state.generating = value
    },


    SET_SAVED_QUERIES(state, queries) {
      state.savedQueries = queries
      state.savedQueriesLoaded = true
      state.savedQueriesUnavailable = false
    },

    SET_SAVED_QUERIES_UNAVAILABLE(state, value) {
      state.savedQueriesUnavailable = value
      state.savedQueriesLoaded = !value
    },

    SET_SAVED_QUERY(state, query) {
      state.savedQuery = query
    },

    RESET(state) {
      Object.assign(state, initialState())
    },
  },

  getters: {
    aliasOptions: (state) => state.aliasOptions,
    locationAliasOptions: (state) => state.locationAliasOptions,
    aliasLabels: (state) => state.aliasOptions.aliasLabels,
    mediaLabels: (state) => state.aliasOptions.mediaLabels,
    qaQcAliases: (state) => state.aliasOptions.aliases.filter((alias) => alias.isQaQc),
    tenantOptions: (state) => state.tenantOptions,
    analyteCatalogue: (state) => state.analyteCatalogue,
    locationOptions: (state) => state.locationOptions,
    gateKind: (state) => state.gateKind,
    resolution: (state) => state.resolution,

    savedQueries: (state) => state.savedQueries,
    savedQueriesUnavailable: (state) => state.savedQueriesUnavailable,

  },

  actions: {
    /*
     * GET api/Rpd/AliasOptions - the QA/QC aliases and the tenant's media types, for both pickers.
     * Loaded once per page; the page gates on it rather than re-requesting per keystroke.
     */
    async loadAliasOptions({ state, commit }) {
      if (state.aliasOptionsLoaded) return state.aliasOptions

      try {
        const res = await axios.get(RPD_PATHS.aliasOptions)
        const options = toAliasOptions(res.data)
        commit('SET_ALIAS_OPTIONS', options)
        return options
      } catch (error) {
        commit('SET_ALIAS_OPTIONS_ERROR', messageFrom(error))
        throw error
      }
    },

    /*
     * GET api/Rpd/AliasOptions?locationId=... - the aliases the named location itself can produce,
     * read from its own synonyms. An alias id is a sample type, so it only means anything against
     * the location it was read from; the two sides therefore keep their own list.
     *
     * The media types ride along unchanged, so this is not a second tenant-wide load and the
     * caller can treat one response as the whole vocabulary for that side.
     */
    async loadLocationAliasOptions({ commit }, { side, locationId }) {
      if (locationId === null || locationId === undefined || locationId === '') {
        commit('SET_LOCATION_ALIAS_OPTIONS', { side, locationId: null, options: null })
        return null
      }

      const res = await axios.get(RPD_PATHS.aliasOptions, { params: { locationId } })
      const options = toAliasOptions(res.data)
      commit('SET_LOCATION_ALIAS_OPTIONS', { side, locationId, options })
      return options
    },

    /*
     * POST api/Rpd/Options - the tenant's acceptance threshold, detection-limit multiplier and
     * merged exclusion list. The per-query overrides are the UI's third layer and are applied on
     * Generate, so what arrives here is the starting point, not the final word.
     */
    async loadTenantOptions({ state, commit }) {
      if (state.tenantOptionsLoaded) return state.tenantOptions

      try {
        const res = await axios.post(RPD_PATHS.options, {})
        const options = toTenantOptions(res.data)
        commit('SET_TENANT_OPTIONS', options)
        return options
      } catch (error) {
        commit('SET_TENANT_OPTIONS_ERROR', messageFrom(error))
        throw error
      }
    },

    /*
     * GET api/Analyte/List - an existing, tenant-scoped route, reused unchanged so the exclusion
     * picker can offer every analyte the tenant owns. Loaded lazily: the picker lives on the
     * second tab, so a reader who never opens it never pays for the catalogue.
     */
    async loadAnalyteCatalogue({ state, commit }) {
      if (state.analyteCatalogueLoaded) return state.analyteCatalogue

      const res = await axios.get(RPD_PATHS.analyteList)
      const analytes = toAnalyteCatalogue(res.data)
      commit('SET_ANALYTE_CATALOGUE', analytes)
      return analytes
    },

    /*
     * GET api/DataSources?sourceTypes=discrete_envirodata - an existing, tenant-scoped route,
     * reused unchanged so both location pickers offer the tenant's discrete stations by name.
     * The datasource module already owns the call and its cache, so this delegates rather than
     * duplicating it; the flat list is projected once here for the two selects.
     */
    async loadLocationOptions({ state, commit, dispatch, rootGetters }) {
      if (state.locationOptionsLoaded) return state.locationOptions

      await dispatch('datasource/fetchDataSources', {
        sourceTypes: [SOURCE_TYPES.discreteEnvirodata],
      }, { root: true })

      const groups = rootGetters['datasource/dataSources'] || {}
      const stations = groups[SOURCE_TYPES.discreteEnvirodata] || []
      const locations = stations.map(toLocationOption)

      commit('SET_LOCATION_OPTIONS', locations)
      return locations
    },

    /*
     * POST api/Rpd/Resolve - the pairing gate. A blocked pairing is not an error: 409 and 422 both
     * carry a usable verdict, so they resolve to a gate state rather than rejecting. Only an
     * unexpected status is rethrown.
     */
    async resolvePairing({ commit }, pairing) {
      commit('SET_RESOLVING', true)

      try {
        const res = await axios.post(RPD_PATHS.resolve, buildResolveRequest(pairing))
        const outcome = toResolveOutcome(res.data, res.status)
        commit('SET_RESOLUTION', outcome)
        return outcome
      } catch (error) {
        const status = error && error.response ? error.response.status : null

        if (status === 409 || status === 422) {
          const outcome = toResolveOutcome(error.response.data, status)
          commit('SET_RESOLUTION', outcome)
          return outcome
        }

        /* A transport or server failure must not leave a stale "ready" on screen. */
        commit('SET_RESOLUTION', { gateKind: GATE_IDLE })
        throw error
      } finally {
        commit('SET_RESOLVING', false)
      }
    },

    /*
     * POST api/Rpd/Generate - accepts the work and answers 202 with a message id. The workbook is
     * produced out of band and the reviewer follows it in the message centre, so there is nothing
     * to record here: the page shows a submitted notice and stops.
     */
    async generate({ commit }, { pairing, thresholds, excludedAnalyteIds }) {
      commit('SET_GENERATING', true)

      try {
        const res = await axios.post(
          RPD_PATHS.generate,
          buildGenerateRequest(pairing, thresholds, excludedAnalyteIds),
        )
        return toJobAccepted(res.data)
      } finally {
        commit('SET_GENERATING', false)
      }
    },

    /* GET api/Rpd/SavedQueries - the drawer's list. A 503 is surfaced as unreachable, not empty. */
    async loadSavedQueries({ commit }) {
      try {
        const res = await axios.get(RPD_PATHS.savedQueries)
        const queries = toSavedQueries(res.data)
        commit('SET_SAVED_QUERIES', queries)
        return queries
      } catch (error) {
        const status = error && error.response ? error.response.status : null
        if (status === 503) {
          commit('SET_SAVED_QUERIES_UNAVAILABLE', true)
          return []
        }
        throw error
      }
    },

    /* GET api/Rpd/SavedQueries/{id} - one stored definition, for the drawer to reopen. */
    async loadSavedQuery({ commit }, id) {
      const res = await axios.get(RPD_PATHS.savedQuery(id))
      const query = res.data
      commit('SET_SAVED_QUERY', query)

      return {
        query,
        pairing: pairingFromConfiguration(query.Configuration),
        thresholds: thresholdsFromConfiguration(query.Configuration),
        excludedAnalyteIds: excludedAnalyteIdsFromConfiguration(query.Configuration),
      }
    },

    /*
     * POST api/Rpd/SavedQueries - a new definition. The server derives the document id from the
     * tenant and the name, so a name that is taken answers 409 with the owning user; the caller
     * re-reads the list and re-renders the dialog rather than guessing.
     */
    async createSavedQuery({ commit }, { name, isShared, description, pairing, thresholds, excludedAnalyteIds, includeMedia }) {
      const payload = {
        Name: name,
        IsShared: !!isShared,
        Description: description || '',
        Configuration: configurationFromPairing(
          pairing, thresholds, excludedAnalyteIds, includeMedia,
        ),
      }

      const res = await axios.post(RPD_PATHS.savedQueries, payload)
      const saved = res.data
      commit('SET_SAVED_QUERY', saved)
      return saved
    },

    /*
     * PUT api/Rpd/SavedQueries/{id} - replaces a definition the caller may overwrite. A rename
     * moves the document id, so the id is recomputed from the new name by the caller before it
     * gets here.
     */
    async updateSavedQuery({ commit }, { id, name, isShared, description, pairing, thresholds, excludedAnalyteIds, includeMedia }) {
      const payload = {
        Name: name,
        IsShared: !!isShared,
        Description: description || '',
        Configuration: configurationFromPairing(
          pairing, thresholds, excludedAnalyteIds, includeMedia,
        ),
      }

      const res = await axios.put(RPD_PATHS.savedQuery(id), payload)
      const saved = res.data
      commit('SET_SAVED_QUERY', saved)
      return saved
    },

    /* DELETE api/Rpd/SavedQueries/{id} - answers 204, so there is no body to read. */
    async deleteSavedQuery({ state, commit }, id) {
      await axios.delete(RPD_PATHS.savedQuery(id))
      commit('SET_SAVED_QUERIES', state.savedQueries.filter((query) => query.Id !== id))
      return true
    },
  },
}

/**
 * The error body this API emits. The response interceptor has already unwrapped `Data` and moved
 * `Message` onto `error.message`, so both shapes are covered.
 */
function messageFrom(error) {
  if (!error) return 'Something went wrong.'

  const data = error.response ? error.response.data : null
  if (data && typeof data === 'object') {
    if (data.Message) return data.Message
    if (data.ExceptionMessage) return data.ExceptionMessage
  }
  if (typeof data === 'string' && data) return data
  return error.message || 'Something went wrong.'
}

export { messageFrom }
